import isNil from 'lodash/isNil';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useScrollPagination } from 'hooks/utility';
import { PAIR_CONNECT_WALLETS_RELOAD, STOP_RELOAD_PREFIX } from 'shared/consts/reload';
import { useTypedDispatch } from 'store';
import { useTypedSelector } from 'store';
import { useFilters } from 'store/slices/filters/hooks/useFilters';
import { dropAlertState, setAlertState } from 'store/slices/ui';
import { Bus } from 'tools';
import { EDexPairFilters } from 'types/filters';
import { IConnectWalletToPairRow } from 'types/tables/dex/connectWalletToPairRow';
import { EExchange } from 'web3';

import { useDexWalletsToConnect } from '../api';

const useDexWalletsToConnectRecords = () => {
  const dispatch = useTypedDispatch();
  const dexPair = useTypedSelector(store => store.pairs.selectedDexPair)!;

  const { settedPairFilters } = useFilters({
    dex: {
      pair: dexPair,
      type: EDexPairFilters.connectWallets,
    },
  });

  const [virtualRecords, setVirtualRecords] = useState<IConnectWalletToPairRow[]>([]);
  const [rowSelection, setRowSelection] = useState<Record<number, boolean>>({});

  const getWallets = useDexWalletsToConnect();

  const getRecords = useCallback(
    async ({
      limit,
      lastSeenId,
    }: {
      limit: number;
      lastSeenId: number;
    }): Promise<
      | {
          records: IConnectWalletToPairRow[];
          has_more: boolean;
        }
      | undefined
    > => {
      try {
        const { isSuccess, errorMessage, data } = await getWallets({
          lastSeenId,
          limit,
          filters: settedPairFilters,
        });

        if (isSuccess && data) {
          return {
            records: data.items,
            has_more: data.has_next,
          };
        } else {
          dispatch(
            setAlertState({
              type: 'failed',
              text: errorMessage ?? 'Something went wrong',
              onClose: () => dispatch(dropAlertState()),
              onSubmit: () => dispatch(dropAlertState()),
            }),
          );
          return undefined;
        }
      } catch (error) {
        console.log(error);
        return undefined;
      }
    },
    [dispatch, getWallets, settedPairFilters],
  );

  const { hasMore, records, setRecords, loading, loadMore, remount } = useScrollPagination({
    limit: 200,
    getRecords,
    remountLoad: true,
  });

  const handleRemount = useCallback(() => {
    setRecords(undefined);
    setRowSelection({});
    remount();
  }, [remount, setRecords]);

  const selectedRecords = useMemo(() => {
    const selectedRows: {
      address: string;
      id: number;
      index: number;
    }[] = [];

    if (!records) return [];

    for (const key of Object.keys(rowSelection)) {
      selectedRows.push(records[Number(key)]);
    }

    return selectedRows;
  }, [rowSelection, records]);

  const updateVisibleRecords = useCallback(async () => {
    const lastSeenId =
      (records ?? []).length < 20
        ? undefined
        : virtualRecords[0]
          ? virtualRecords[0].id
          : undefined;
    const limit = virtualRecords.length;

    if (limit === 0 || !records) {
      Bus.emit(STOP_RELOAD_PREFIX + PAIR_CONNECT_WALLETS_RELOAD);
      return;
    }

    try {
      const startTime = Date.now();

      const { isSuccess, errorMessage, data } = await getWallets({
        lastSeenId: lastSeenId ? lastSeenId - 1 : undefined,
        limit,
        filters: settedPairFilters,
      });

      const endTime = Date.now();

      if (isSuccess && data) {
        const firstSeenIndex = isNil(lastSeenId)
          ? 0
          : records.findIndex(el => el.id === lastSeenId);
        const newRecords = [...records];

        newRecords.splice(firstSeenIndex, data.items.length, ...data.items);

        setRecords(newRecords);

        if (endTime - startTime >= 1500) {
          Bus.emit(STOP_RELOAD_PREFIX + PAIR_CONNECT_WALLETS_RELOAD);
        } else {
          setTimeout(
            () => {
              Bus.emit(STOP_RELOAD_PREFIX + PAIR_CONNECT_WALLETS_RELOAD);
            },
            1500 - (endTime - startTime),
          );
        }
      } else {
        dispatch(
          setAlertState({
            type: 'failed',
            text: errorMessage ?? 'Something went wrong',
            onClose: () => dispatch(dropAlertState()),
            onSubmit: () => dispatch(dropAlertState()),
          }),
        );
        Bus.emit(STOP_RELOAD_PREFIX + PAIR_CONNECT_WALLETS_RELOAD);
      }
    } catch (error) {
      console.log(error);
      Bus.emit(STOP_RELOAD_PREFIX + PAIR_CONNECT_WALLETS_RELOAD);
    }
  }, [virtualRecords, records, dispatch, getWallets, settedPairFilters, setRecords]);

  useEffect(() => {
    const onFilter = () => {
      handleRemount();
    };

    Bus.on(`${EExchange.dex}:${EDexPairFilters.connectWallets}:FILTER`, onFilter);

    return () => {
      Bus.off(`${EExchange.dex}:${EDexPairFilters.connectWallets}:FILTER`, onFilter);
    };
  }, [handleRemount]);

  useEffect(() => {
    Bus.on(PAIR_CONNECT_WALLETS_RELOAD, updateVisibleRecords);

    return () => {
      Bus.off(PAIR_CONNECT_WALLETS_RELOAD, updateVisibleRecords);
    };
  }, [updateVisibleRecords]);

  const result = useMemo(
    () => ({
      tableRecords: records ?? [],
      records: { get: records, set: setRecords },
      virtualRecords: { get: virtualRecords, set: setVirtualRecords },
      hasMore,
      loading,
      loadMore,
      handleRemount,
      rowSelection: { get: rowSelection, set: setRowSelection },
      selectedRecords,
    }),
    [
      records,
      setRecords,
      hasMore,
      loading,
      loadMore,
      handleRemount,
      selectedRecords,
      rowSelection,
      setRowSelection,
      virtualRecords,
      setVirtualRecords,
    ],
  );

  return result;
};

export { useDexWalletsToConnectRecords };
